// services/mentorChatService.ts
// Service for handling the mentor AI chat streaming API
// Provides real-time streaming responses using Server-Sent Events (SSE)

import { Platform } from 'react-native';
import { auth } from '../config/firebase';
import { FirebaseFunctionsAPI } from './firebaseFunctionsAPI';
import { PrivateProfileService } from './privateProfileService';
import { ChatWithRecommendationsSchema } from '../utils/mentorSchemas';
import { errorReporting } from './errorReportingService';
import { 
  PrivateProfile, 
  MentorChatHistory, 
  DailyRecommendations, 
  CoreProfile, 
  ActiveContext, 
  ProfileSummary 
} from '../models/PrivateProfile';

// === Type Definitions ===

export interface ChatHistoryEntry {
  role: 'user' | 'assistant';
  message: string;
}

export interface DashboardContext {
  currentBooks: Array<{
    bookId: string;
    title: string;
    reason: string;
  }>;
  currentInsights: Array<{
    insightId: string;
    title: string;
    bookId: string;
  }>;
  currentMessage: string;
  lastQuery?: string;
  hasPersonalizedContent: boolean;
  contentGeneratedAt?: number;
}

export interface UserProfile {
  uid: string;
  languageCode?: string;
  audiences?: string[];
  coreProfile?: {
    goals?: string[];
    challenges?: string[];
    values?: string[];
    motivations?: string[];
    interests?: string[];
    personalityTraits?: string[];
    mainFocusLabel?: string;
    mainFocusCategory?: string;
  };
  activeContext?: ActiveContext;
  finishedContent?: {
    bookIds: string[];
    insightIds: string[];
  };
}

// New gateway request/response contracts
export type MentorGatewayRole = 'system' | 'user' | 'assistant';

export interface MentorGatewayMessage {
  role: MentorGatewayRole;
  content: string;
}

export interface MentorGatewayResponse<T = any> {
  success: boolean;
  mentorId: string;
  data?: T;
  text?: string;
  error?: string;
}

export interface MentorGatewayStreamCallbacks<T = any> {
  onDelta?: (delta: string) => void;
  onResult?: (result: T) => void;
  onComplete?: () => void;
  onError?: (error: string) => void;
}

export interface MentorGatewayRequestOptions {
  modelPreference?: 'fast' | 'standard' | 'reasoning';
  promptStyle?: 'brief' | 'detailed' | 'detailed_with_books';
  responseStyle?: 'ack' | 'short' | 'normal' | 'long';
  maxOutputTokens?: number;
  responseLanguage?: string;
  audiences?: string[];
  audienceFilterMode?: 'strict' | 'off';
  
  // Retrieval controls (optional - native vector search)
  contextPreference?: 'books' | 'insights' | 'mixed';
  contextTags?: string[];         // [Legacy] Not recommended - use natural language instead
  contextAudience?: string[];     // [Legacy] Not recommended - use natural language instead
  contextLimit?: number;          // default: 8
  minContextScore?: number;       // default: 0.2
}

// === Callback Types ===
export interface StreamCallbacks {
  onTextDelta?: (textDelta: string) => void;
  onRecommendations?: (recommendations: DailyRecommendations) => void;
  onComplete?: (fullText: string, recommendations?: DailyRecommendations) => void;
  onError?: (error: string) => void;
}

export class MentorChatService extends FirebaseFunctionsAPI {
  private static instance: MentorChatService;

  private constructor() {
    super();
  }

  static getInstance(): MentorChatService {
    if (!MentorChatService.instance) {
      MentorChatService.instance = new MentorChatService();
    }
    return MentorChatService.instance;
  }

  /**
   * Convert PrivateProfile to backend UserProfile format
   * Server uses this for personalization and context
   */
  static createUserProfile(profile: PrivateProfile): UserProfile {
    // Get finished content from user progress
    const finishedBookIds = Object.keys(profile.booksProgress).filter(
      bookId => profile.booksProgress[bookId].isCompleted
    );
    
    const finishedInsightIds = Object.keys(profile.insightsProgress).filter(
      insightId => profile.insightsProgress[insightId].isCompleted
    );

    return {
      uid: profile.id,
      languageCode: 'en', // Default to English for now (caller may override via options.responseLanguage)
      audiences: profile.audiences || [],
      coreProfile: {
        goals: profile.coreProfile?.goals || [],
        challenges: profile.coreProfile?.challenges || [],
        values: (profile.coreProfile as any)?.values || [],
        motivations: [], // Could be extracted from onboarding in future
        interests: profile.coreProfile?.interests || [],
        personalityTraits: profile.coreProfile?.personalityTraits || [],
        mainFocusLabel: profile.coreProfile?.mainFocusLabel,
        mainFocusCategory: profile.coreProfile?.mainFocusCategory,
      },
      activeContext: profile.activeContext,
      finishedContent: {
        bookIds: finishedBookIds,
        insightIds: finishedInsightIds,
      },
    };
  }

  /**
   * Collect all previous recommendations from chat history and current recommendations
   */
  static collectPreviousRecommendations(
    chatHistory: MentorChatHistory[],
    profile: PrivateProfile
  ): { books: Array<{ bookId: string; reason: string; recommendedAt: number }>; insights: Array<{ insightId: string; reason: string; bookId?: string; recommendedAt: number }> } {
    const books = new Map<string, { bookId: string; reason: string; recommendedAt: number }>();
    const insights = new Map<string, { insightId: string; reason: string; bookId?: string; recommendedAt: number }>();
    
    // Collect from recent chat history (last 20 interactions to get good context)
    const recentHistory = chatHistory.slice(-20);
    recentHistory.forEach(chat => {
      if (chat.recommendations?.books) {
        chat.recommendations.books.forEach(book => {
          const baseId = book.bookId?.split('_')[0] || book.bookId;
          if (baseId) {
            books.set(baseId, {
              bookId: baseId,
              reason: book.reason,
              recommendedAt: book.recommendedAt
            });
          }
        });
      }
      if (chat.recommendations?.insights) {
        chat.recommendations.insights.forEach(insight => {
          insights.set(insight.insightId, {
            insightId: insight.insightId,
            reason: insight.reason,
            bookId: insight.bookId,
            recommendedAt: insight.recommendedAt
          });
        });
      }
    });
    
    // Also include current dashboard recommendations
    if (profile.currentRecommendations?.recommendedBooks) {
      profile.currentRecommendations.recommendedBooks.forEach(book => {
        const baseId = book.bookId?.split('_')[0] || book.bookId;
        if (baseId) {
          books.set(baseId, {
            bookId: baseId,
            reason: book.reason,
            recommendedAt: book.recommendedAt
          });
        }
      });
    }
    if (profile.currentRecommendations?.insightPlan?.insights) {
      profile.currentRecommendations.insightPlan.insights.forEach(insight => {
        insights.set(insight.insightId, {
          insightId: insight.insightId,
          reason: insight.reason,
          bookId: insight.bookId,
          recommendedAt: insight.recommendedAt
        });
      });
    }
    
    return {
      books: Array.from(books.values()),
      insights: Array.from(insights.values())
    };
  }

  /**
   * Convert MentorChatHistory to backend ChatHistoryEntry format
   * Clean version without text-based recommendations (using structured data instead)
   */
  static convertChatHistory(chatHistory: MentorChatHistory[]): ChatHistoryEntry[] {
    return chatHistory.flatMap(chat => [
      { role: 'user' as const, message: chat.userMessage },
      { role: 'assistant' as const, message: chat.assistantResponse }
    ]);
  }

  static normalizeRecommendations(recommendations?: {
    books?: Array<{ bookId: string | number; reason: string }>;
    insights?: Array<{ insightId: string | number; reason: string }>;
  }) {
    if (!recommendations) return undefined;
    const books = (recommendations.books || []).map(book => ({
      ...book,
      bookId: typeof book.bookId === 'number' ? book.bookId.toString() : book.bookId
    }));
    const insights = (recommendations.insights || []).map(insight => ({
      ...insight,
      insightId: typeof insight.insightId === 'number' ? insight.insightId.toString() : insight.insightId
    }));
    return { books, insights };
  }

  // ========= New mentorGateway methods =========

  private async callMentorGateway<T = any>(
    messages: MentorGatewayMessage[],
    outputSchema?: any,
    options?: MentorGatewayRequestOptions,
    userProfile?: UserProfile,
    searchQuery?: string
  ): Promise<MentorGatewayResponse<T>> {
    const url = this.getCallableUrl('mentorGateway');
    
    // Build request body - server owns mentorId and promptStyle selection
    const body: Record<string, any> = { 
      messages,
      // Server auto-selects general_mentor + primary.md (no searchQuery) or with_books.md (with searchQuery)
    };
    
    if (outputSchema) body.outputSchema = outputSchema;
    if (userProfile) body.userProfile = userProfile;
    if (searchQuery) body.searchQuery = searchQuery; // Presence triggers Phase 2 (with_books.md prompt)
    if (options?.modelPreference) body.modelPreference = options.modelPreference;
    if (options?.responseStyle) body.responseStyle = options.responseStyle;
    if (typeof options?.maxOutputTokens === 'number') body.maxOutputTokens = options.maxOutputTokens;
    if (options?.responseLanguage) body.responseLanguage = options.responseLanguage;
    if (options?.audiences) body.audiences = options.audiences;
    if (options?.audienceFilterMode) body.audienceFilterMode = options.audienceFilterMode;
    
    // RAG/Retrieval controls (optional advanced features)
    if (options?.contextPreference) body.contextPreference = options.contextPreference;
    if (options?.contextTags) body.contextTags = options.contextTags;
    if (options?.contextAudience) body.contextAudience = options.contextAudience;
    if (typeof options?.contextLimit === 'number') body.contextLimit = options.contextLimit;
    if (typeof options?.minContextScore === 'number') body.minContextScore = options.minContextScore;

    // Capture diagnostic information for error reporting
    const diagnostics = {
      url,
      timestamp: new Date().toISOString(),
      hasAuth: false,
      userId: userProfile?.uid || auth.currentUser?.uid || 'none',
      isAnonymous: auth.currentUser?.isAnonymous,
      platform: Platform.OS,
      hasSchema: !!outputSchema,
      hasSearchQuery: !!searchQuery,
      messageCount: messages.length,
      requestOptions: {
        modelPreference: options?.modelPreference,
        responseStyle: options?.responseStyle,
        maxOutputTokens: options?.maxOutputTokens,
      },
    };

    let authHeaders: Record<string, string>;
    try {
      authHeaders = await this.getAuthHeaders();
      diagnostics.hasAuth = !!authHeaders.Authorization;
    } catch (authError) {
      console.error('❌ Failed to get auth headers:', authError);
      throw new Error(`Authentication failed: ${authError instanceof Error ? authError.message : String(authError)}`);
    }

    let response: Response;
    let fetchError: Error | null = null;
    
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(body),
      });
    } catch (error) {
      // Network-level failure (can't reach server at all)
      fetchError = error instanceof Error ? error : new Error(String(error));
      
      console.error('❌ mentorGateway fetch failed:', {
        error: fetchError.message,
        errorName: fetchError.name,
        diagnostics,
      });

      // Report with full diagnostic context
      errorReporting.reportError(fetchError, {
        userId: diagnostics.userId,
        screen: 'MentorChatService',
        action: 'callMentorGateway_network_failure',
        metadata: {
          ...diagnostics,
          errorType: 'NETWORK_FETCH_FAILED',
          errorMessage: fetchError.message,
          errorName: fetchError.name,
          errorStack: fetchError.stack,
          // Check if it's a CORS issue
          possibleCORS: fetchError.message.includes('CORS') || fetchError.message.includes('Load failed'),
          // Network connectivity info
          onLine: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
        },
      });

      throw new Error(`Network request failed: ${fetchError.message}. URL: ${url}. Please check your internet connection and try again.`);
    }

    // Parse response
    let json: any;
    let responseText: string = '';
    
    try {
      responseText = await response.text();
      json = JSON.parse(responseText);
    } catch (parseError) {
      const parseErrorObj = parseError instanceof Error ? parseError : new Error(String(parseError));
      
      console.error('❌ mentorGateway response parse failed:', {
        status: response.status,
        statusText: response.statusText,
        responsePreview: responseText.substring(0, 500),
        parseError: parseErrorObj.message,
        diagnostics,
      });

      errorReporting.reportError(parseErrorObj, {
        userId: diagnostics.userId,
        screen: 'MentorChatService',
        action: 'callMentorGateway_parse_failure',
        metadata: {
          ...diagnostics,
          errorType: 'RESPONSE_PARSE_FAILED',
          httpStatus: response.status,
          httpStatusText: response.statusText,
          responsePreview: responseText.substring(0, 500),
          responseLength: responseText.length,
          contentType: response.headers.get('content-type'),
        },
      });

      throw new Error(`Failed to parse server response (HTTP ${response.status}). This might indicate a server error.`);
    }

    // Check for HTTP errors or unsuccessful responses
    if (!response.ok || !json.success) {
      const errMsg = json.error || `HTTP ${response.status}: ${response.statusText}`;
      
      console.error('❌ mentorGateway returned error:', {
        status: response.status,
        statusText: response.statusText,
        errorMessage: errMsg,
        responseData: json,
        diagnostics,
      });

      errorReporting.reportError(new Error(errMsg), {
        userId: diagnostics.userId,
        screen: 'MentorChatService',
        action: 'callMentorGateway_http_error',
        metadata: {
          ...diagnostics,
          errorType: 'HTTP_ERROR',
          httpStatus: response.status,
          httpStatusText: response.statusText,
          serverError: json.error,
          responseData: json,
          // Identify specific error types
          isAuthError: response.status === 401 || response.status === 403,
          isNotFound: response.status === 404,
          isServerError: response.status >= 500,
        },
      });

      throw new Error(errMsg);
    }

    return json as MentorGatewayResponse<T>;
  }

  async streamMentorGateway<T = any>(
    messages: MentorGatewayMessage[],
    callbacks: MentorGatewayStreamCallbacks<T>,
    outputSchema?: any,
    options?: MentorGatewayRequestOptions,
    userProfile?: UserProfile,
    searchQuery?: string
  ): Promise<void> {
    const url = this.getCallableUrl('mentorGateway');
    const body: Record<string, any> = { messages, stream: true };
    if (outputSchema) body.outputSchema = outputSchema;
    if (userProfile) body.userProfile = userProfile;
    if (searchQuery) body.searchQuery = searchQuery;
    if (options?.modelPreference) body.modelPreference = options.modelPreference;
    if (options?.responseStyle) body.responseStyle = options.responseStyle;
    if (typeof options?.maxOutputTokens === 'number') body.maxOutputTokens = options.maxOutputTokens;
    if (options?.responseLanguage) body.responseLanguage = options.responseLanguage;
    if (options?.audiences) body.audiences = options.audiences;
    if (options?.audienceFilterMode) body.audienceFilterMode = options.audienceFilterMode;
    
    // RAG/Retrieval controls (optional)
    if (options?.contextPreference) body.contextPreference = options.contextPreference;
    if (options?.contextTags) body.contextTags = options.contextTags;
    if (options?.contextAudience) body.contextAudience = options.contextAudience;
    if (typeof options?.contextLimit === 'number') body.contextLimit = options.contextLimit;
    if (typeof options?.minContextScore === 'number') body.minContextScore = options.minContextScore;

    // Capture diagnostic information for error reporting
    const diagnostics = {
      url,
      timestamp: new Date().toISOString(),
      streaming: true,
      hasAuth: false,
      userId: userProfile?.uid || auth.currentUser?.uid || 'none',
      isAnonymous: auth.currentUser?.isAnonymous,
      platform: Platform.OS,
      hasSchema: !!outputSchema,
      hasSearchQuery: !!searchQuery,
      messageCount: messages.length,
    };

    try {
      const authHeaders = await this.getAuthHeaders();
      diagnostics.hasAuth = !!authHeaders.Authorization;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok || !response.body) {
        const errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        
        console.error('❌ mentorGateway streaming failed:', {
          status: response.status,
          statusText: response.statusText,
          diagnostics,
        });

        errorReporting.reportError(new Error(errorMsg), {
          userId: diagnostics.userId,
          screen: 'MentorChatService',
          action: 'streamMentorGateway_http_error',
          metadata: {
            ...diagnostics,
            errorType: 'STREAMING_HTTP_ERROR',
            httpStatus: response.status,
            httpStatusText: response.statusText,
            hasResponseBody: !!response.body,
          },
        });

        throw new Error(errorMsg);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentEvent: string | null = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const rawLine of lines) {
          const line = rawLine.trim();
          if (line.startsWith('event: ')) {
            currentEvent = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.substring(6);
            try {
              const parsed = JSON.parse(dataStr);
              if (currentEvent === 'delta') {
                const delta: string = typeof parsed === 'string' ? parsed : (parsed.delta ?? '');
                if (delta) callbacks.onDelta?.(delta);
              } else if (currentEvent === 'result') {
                const payload = (parsed && typeof parsed === 'object' && 'data' in parsed) ? (parsed as any).data : parsed;
                callbacks.onResult?.(payload as T);
              } else if (currentEvent === 'complete') {
                callbacks.onComplete?.();
              } else if (currentEvent === 'error') {
                const err = typeof parsed === 'string' ? parsed : (parsed.error ?? 'Unknown streaming error');
                callbacks.onError?.(err);
              }
            } catch (e) {
              // Ignore partial JSON parse errors during streaming
            }
          }
        }
      }
      // Ensure completion callback if server ended without explicit complete
      callbacks.onComplete?.();
    } catch (error) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      const errorMessage = errorObj.message;
      
      console.error('❌ mentorGateway streaming error:', {
        error: errorMessage,
        errorName: errorObj.name,
        diagnostics,
      });

      errorReporting.reportError(errorObj, {
        userId: diagnostics.userId,
        screen: 'MentorChatService',
        action: 'streamMentorGateway_error',
        metadata: {
          ...diagnostics,
          errorType: 'STREAMING_ERROR',
          errorMessage,
          errorName: errorObj.name,
          errorStack: errorObj.stack,
          onLine: typeof navigator !== 'undefined' ? navigator.onLine : 'unknown',
        },
      });

      callbacks.onError?.(errorMessage);
    }
  }

  /**
   * Legacy method - kept for debug screens
   * @deprecated Use chatPhase1/chatPhase2 for production
   */
  async sendChatText(
    userMessage: string,
    profile: PrivateProfile,
    chatHistory: MentorChatHistory[] = [],
    options?: MentorGatewayRequestOptions
  ): Promise<string> {
    const historyMessages = MentorChatService
      .convertChatHistory(chatHistory.slice(-10))
      .map(h => ({ role: h.role, content: h.message }));
    const userProfile = MentorChatService.createUserProfile(profile);
    
    // No system message - server owns all prompts
    const messages: MentorGatewayMessage[] = [
      ...historyMessages,
      { role: 'user', content: userMessage },
    ];
    
    const result = await this.callMentorGateway(messages, undefined, {
      responseStyle: options?.responseStyle ?? 'short',
      modelPreference: options?.modelPreference ?? 'fast',
      maxOutputTokens: options?.maxOutputTokens,
      responseLanguage: options?.responseLanguage || userProfile.languageCode,
    }, userProfile);
    
    if (typeof result.text !== 'string') {
      throw new Error('Gateway did not return text');
    }
    return result.text;
  }

  /**
   * @deprecated Legacy single-phase method - DO NOT USE
   * Use chatPhase1 + chatPhase2 instead for production
   * Kept only for backward compatibility with old debug screens
   */
  async sendChatWithRecommendations(
    userMessage: string,
    profile: PrivateProfile,
    chatHistory: MentorChatHistory[] = [],
    options?: MentorGatewayRequestOptions
  ): Promise<{ 
    text: string; 
    recommendations?: { 
      books?: Array<{ bookId: string; reason: string }>; 
      insights?: Array<{ insightId: string; reason: string }> 
    };
  }> {
    console.warn('[DEPRECATED] sendChatWithRecommendations is deprecated. Use chatPhase1 + chatPhase2 instead.');
    
    // Minimal implementation using Phase 1 only
    const historyMessages = MentorChatService
      .convertChatHistory(chatHistory.slice(-10))
      .map(h => ({ role: h.role as 'user' | 'assistant', content: h.message }));
    
    const userProfile = MentorChatService.createUserProfile(profile);
    
    const messages = [
      ...historyMessages,
      { role: 'user' as const, content: userMessage },
    ];

    const result = await this.callMentorGateway<any>(messages, ChatWithRecommendationsSchema, {
      responseStyle: options?.responseStyle ?? 'normal',
      modelPreference: options?.modelPreference ?? 'fast',
      maxOutputTokens: options?.maxOutputTokens,
      responseLanguage: options?.responseLanguage || userProfile.languageCode,
    }, userProfile);

    return {
      text: result.data?.text || result.text || '',
      recommendations: {
        books: result.data?.books || [],
        insights: result.data?.insights || []
      }
    };
  }

  /**
   * Legacy method - kept for onboarding acknowledgements
   * @deprecated Use chatPhase1/chatPhase2 for production chat
   */
  async sendText(
    messages: MentorGatewayMessage[],
    options?: MentorGatewayRequestOptions
  ): Promise<string> {
    // Strip any system messages - server owns all prompts
    const cleanMessages = messages.filter(m => m.role !== 'system');
    
    const result = await this.callMentorGateway(cleanMessages, undefined, {
      responseStyle: options?.responseStyle ?? 'short',
      modelPreference: options?.modelPreference ?? 'fast',
      maxOutputTokens: options?.maxOutputTokens,
      responseLanguage: options?.responseLanguage,
    });
    if (typeof result.text !== 'string') {
      throw new Error('Gateway did not return text');
    }
    return result.text;
  }

  /**
   * Legacy method - kept for special cases
   * @deprecated Use chatPhase1/chatPhase2
   */
  async sendStructuredRequest<T = any>(
    messages: MentorGatewayMessage[],
    outputSchema: any,
    options?: MentorGatewayRequestOptions,
    userProfile?: UserProfile
  ): Promise<T> {
    // Strip any system messages - server owns all prompts
    const cleanMessages = messages.filter(m => m.role !== 'system');
    
    const result = await this.callMentorGateway<T>(cleanMessages, outputSchema, {
      responseStyle: options?.responseStyle ?? 'normal',
      modelPreference: options?.modelPreference ?? 'fast',
      maxOutputTokens: options?.maxOutputTokens,
      responseLanguage: options?.responseLanguage,
    }, userProfile);
    if (result.data === undefined) {
      throw new Error('Gateway did not return structured data');
    }
    return result.data as T;
  }

  /**
   * Phase 1: Chat - Get quick response with optional search query
   * Returns: text, searchQuery (empty string if not needed), profileChanges array
   * AI decides if recommendations are needed
   * No retrieval happens (fast ~15-20s)
   * Server uses primary.md prompt
   */
  async chatPhase1(
    messages: MentorGatewayMessage[],
    outputSchema: any,
    options?: MentorGatewayRequestOptions,
    userProfile?: UserProfile
  ): Promise<{
    text: string;
    searchQuery: string;
    profileChanges: Array<{ operation: string; field: string; value: string }>;
  }> {
    // Strip any system messages - server owns all prompts
    const cleanMessages = messages.filter(m => m.role !== 'system');
    
    const result = await this.callMentorGateway<any>(
      cleanMessages,
      outputSchema,
      {
        responseStyle: options?.responseStyle ?? 'short',
        modelPreference: options?.modelPreference ?? 'fast',
        maxOutputTokens: options?.maxOutputTokens,
        responseLanguage: options?.responseLanguage,
      },
      userProfile,
      undefined // No searchQuery = Phase 1 (server uses primary.md)
    );

    if (!result.data) {
      throw new Error('Phase 1 did not return data');
    }

    return result.data;
  }

  /**
   * Phase 2: Chat - Get recommendations using searchQuery from phase 1
   * Returns: text (brief intro), books, insights
   * Uses searchQuery to skip intent extraction (saves ~12-14s)
   * Server uses with_books.md prompt
   */
  async chatPhase2(
    messages: MentorGatewayMessage[],
    searchQuery: string,
    outputSchema: any,
    options?: MentorGatewayRequestOptions,
    userProfile?: UserProfile
  ): Promise<{
    text: string;
    books: Array<{ bookId: string; reason: string }>;
    insights: Array<{ insightId: string; reason: string }>;
  }> {
    // Strip any system messages - server owns all prompts
    const cleanMessages = messages.filter(m => m.role !== 'system');
    
    const result = await this.callMentorGateway<any>(
      cleanMessages,
      outputSchema,
      {
        responseStyle: options?.responseStyle ?? 'short',
        modelPreference: options?.modelPreference ?? 'fast',
        maxOutputTokens: options?.maxOutputTokens,
        responseLanguage: options?.responseLanguage,
      },
      userProfile,
      searchQuery // Provide searchQuery = Phase 2 (server uses with_books.md)
    );

    if (!result.data) {
      throw new Error('Phase 2 did not return data');
    }

    return result.data;
  }

  /**
   * Generate hyper-personalized message for a single book
   * Used when existing recommendation reason doesn't mention user's name or profile
   * 
   * @param bookId - Book ID
   * @param bookTitle - Book title
   * @param bookAuthor - Book author
   * @param bookDescription - Book introduction or biggestTakeaway (what's inside)
   * @param keyInsightsTitles - Array of key insight titles (optional but helps contextualize)
   * @param userProfile - User profile with goals, challenges, etc.
   * @param userName - User's first name
   * @returns Personalized message explaining why this book is perfect for this user
   */
  async generateBookPersonalization(
    bookId: string,
    bookTitle: string,
    bookAuthor: string,
    bookDescription: string | undefined,
    keyInsightsTitles: string[] | undefined,
    userProfile: UserProfile,
    userName: string
  ): Promise<{ success: boolean; message?: string; error?: string }> {
    try {
      const { BookPersonalizationSchema } = require('../utils/mentorSchemas');
      
      // Build focused prompt with book context
      const goals = userProfile.coreProfile?.goals?.join(', ') || 'personal growth';
      const challenges = userProfile.coreProfile?.challenges?.join(', ') || 'none specified';
      const interests = userProfile.coreProfile?.interests?.join(', ') || 'self-improvement';
      const values = userProfile.coreProfile?.values?.join(', ');
      const traits = userProfile.coreProfile?.personalityTraits?.join(', ');
      const focus = userProfile.coreProfile?.mainFocusLabel || userProfile.coreProfile?.mainFocusCategory;
      const background = userProfile.activeContext?.information;
      
      // Build book context section
      let bookContext = `"${bookTitle}" by ${bookAuthor}`;
      if (bookDescription) {
        // Truncate description to ~300 chars for context
        const truncatedDesc = bookDescription.length > 300 
          ? bookDescription.substring(0, 300) + '...'
          : bookDescription;
        bookContext += `\n\nWhat this book offers: ${truncatedDesc}`;
      }
      if (keyInsightsTitles && keyInsightsTitles.length > 0) {
        // Include up to 5 key insights
        const insightsList = keyInsightsTitles.slice(0, 5).map(t => `- ${t}`).join('\n');
        bookContext += `\n\nKey topics covered:\n${insightsList}`;
      }
      
      const prompt = `User context:
- Name: ${userName}
- Goals: ${goals}
- Challenges: ${challenges}
- Interests: ${interests}

Book: ${bookContext}

Write a natural, conversational message (1-2 sentences) connecting this book to ${userName}'s specific situation. Mention their name naturally somewhere in the message, reference their actual goals or challenges, and vary your phrasing—don't use formulaic phrases like "perfect for you" or "this book will help you". Speak directly about what the book offers for their specific needs.

Examples of good variety:
- "Since you're working on X, this book's approach to Y will give you..."
- "With your challenge around X, you'll find the strategies on Y particularly useful..."
- "The framework on X directly addresses your goal of Y..."
- "You mentioned wanting to X—this book breaks down exactly how to..."

Write something natural and specific to their situation.`;

      const messages: MentorGatewayMessage[] = [
        { role: 'user', content: prompt }
      ];

      const result = await this.callMentorGateway<{ personalizedMessage: string }>(
        messages,
        BookPersonalizationSchema,
        {
          responseStyle: 'short',
          modelPreference: 'fast',
          maxOutputTokens: 150,
          responseLanguage: userProfile.languageCode || 'en',
        }
      );

      if (!result.data?.personalizedMessage) {
        throw new Error('Gateway did not return personalizedMessage');
      }

      return {
        success: true,
        message: result.data.personalizedMessage,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[MentorChatService] generateBookPersonalization error:', errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Generate hyper-personalized messages for multiple books in one call
   * Helps avoid repetitive phrasing from multiple parallel requests.
   */
  async generateBookPersonalizationBatch(
    books: Array<{
      bookId: string;
      title: string;
      author: string;
      description?: string;
      insightTitles?: string[];
    }>,
    userProfile: UserProfile,
    userName: string
  ): Promise<{ success: boolean; messages?: Record<string, string>; error?: string }> {
    if (books.length === 0) {
      return { success: true, messages: {} };
    }

    try {
      const { BookPersonalizationBatchSchema } = require('../utils/mentorSchemas');

      const goals = userProfile.coreProfile?.goals?.join(', ') || 'personal growth';
      const challenges = userProfile.coreProfile?.challenges?.join(', ') || 'none specified';
      const interests = userProfile.coreProfile?.interests?.join(', ') || 'self-improvement';
      const values = userProfile.coreProfile?.values?.join(', ');
      const traits = userProfile.coreProfile?.personalityTraits?.join(', ');
      const focus = userProfile.coreProfile?.mainFocusLabel || userProfile.coreProfile?.mainFocusCategory;
      const background = userProfile.activeContext?.information;

      const booksContext = books.map((book, index) => {
        const titleLine = `${index + 1}. ${book.title} by ${book.author} (id: ${book.bookId})`;
        const descriptionLine = book.description
          ? `What this book offers: ${
              book.description.length > 300 ? `${book.description.substring(0, 300)}...` : book.description
            }`
          : undefined;
        const insightsLine = book.insightTitles && book.insightTitles.length > 0
          ? `Key topics: ${book.insightTitles.slice(0, 5).join('; ')}`
          : undefined;

        return [titleLine, descriptionLine, insightsLine].filter(Boolean).join('\n');
      }).join('\n\n');

      const prompt = `User context (use real specifics, not generic statements):
- Name: ${userName}
- Goals: ${goals}
- Challenges: ${challenges}
- Interests: ${interests}
${values ? `- Values: ${values}\n` : ''}${traits ? `- Personality: ${traits}\n` : ''}${focus ? `- Main focus: ${focus}\n` : ''}${background ? `- Background/context: ${background}\n` : ''}

Write a distinct, 1-2 sentence message for each book below. Anchor each message to a concrete item from this user's context (goal/challenge/focus/value/personality/background). Vary openings and tone; sometimes lead with the user's challenge, sometimes with a goal, sometimes with an interest. Mention ${userName} naturally but not always first. Avoid template phrases and avoid repeating the same sentence structure across books.

Return JSON with personalizations array where each item includes bookId and personalizedMessage. Keep bookId exactly as provided.

Books:
${booksContext}`;

      const messages: MentorGatewayMessage[] = [
        { role: 'user', content: prompt }
      ];

      const result = await this.callMentorGateway<{
        personalizations: Array<{ bookId: string; personalizedMessage: string }>;
      }>(
        messages,
        BookPersonalizationBatchSchema,
        {
          responseStyle: 'short',
          modelPreference: 'fast',
          maxOutputTokens: Math.min(120 * books.length, 900),
          responseLanguage: userProfile.languageCode || 'en',
        }
      );

      const personalizations = result.data?.personalizations || [];
      const mappedMessages: Record<string, string> = {};
      personalizations.forEach(item => {
        if (item.bookId && item.personalizedMessage) {
          mappedMessages[item.bookId] = item.personalizedMessage;
        }
      });

      return {
        success: true,
        messages: mappedMessages,
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[MentorChatService] generateBookPersonalizationBatch error:', errorMessage);
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  // ========= Helpers for composing messages =========

  /**
   * Create a chat request for daily recommendations
   */
  static buildOnboardingMessage(
    questionsAndAnswers: Array<{ question: string; answer: any; type: string }>
  ): string {
    return `I just completed onboarding. Here are my responses:\n${
      questionsAndAnswers.map(qa => `${qa.question}: ${
        Array.isArray(qa.answer) ? qa.answer.join(', ') : qa.answer
      }`).join('\n')
    }`;
  }

  /**
   * Create a request for onboarding completion
   * Uses generateChatResponse for streaming support
   */
  static buildOnboardingMessages(
    questionsAndAnswers: Array<{ question: string; answer: any; type: string }>
  ): MentorGatewayMessage[] {
    return [
      {
        role: 'user',
        content: `${this.buildOnboardingMessage(questionsAndAnswers)}\n\nPlease create a concise profile summary and, if helpful, a simple action plan.`,
      },
    ];
  }
}

export const mentorChatService = MentorChatService.getInstance(); 